import CFG from '../gameLoopConfig.json';
import { WORLD_WIDTH, WORLD_HEIGHT, PLAYER_SIZE, BOSS_SPAWN_INTERVAL, TIER_BOSS_SIZE, TIER_BOSS_WARNING_SEC, ENEMY_DAMAGE } from '../../../constants';
import { SPELLS, SPELL_MAP, weightedSpellPick } from '../../../data/spells';

const TC = CFG.tierTracking;

export function updateTierTracking(s, timestamp, elapsed, ctx) {
  const tierIdx = Math.floor(elapsed / TC.secondsPerTier);
  if (tierIdx > (s.currentTier ?? 0)) {
    s.currentTier      = tierIdx;
    s.tierAnnouncement = { tier: tierIdx, startTime: timestamp };

    // Tier Boss warning every N tiers
    if (tierIdx > 0 && tierIdx % TC.tierBossEveryNTiers === 0 && tierIdx !== (s.lastTierBossAt ?? -1)) {
      s.lastTierBossAt  = tierIdx;
      s.tierBossWarning = { tier: tierIdx, startTime: timestamp };
    }

    // Magic selection every 2 tiers starting at tier 2
    if (tierIdx >= TC.magicSelectStartsAtTier &&
        tierIdx % TC.magicSelectEveryNTiers === 0 &&
        tierIdx !== s.lastMagicTier) {
      s.lastMagicTier = tierIdx;
      const owned  = new Set(s.runSpells ?? []);
      const levels = s.runSpellLevels ?? {};
      const isFull = owned.size >= TC.maxSpellSlots;

      if (isFull) {
        const upgradeable = [...owned]
          .map(id => SPELL_MAP[id])
          .filter(sp => sp && (levels[sp.id] ?? 1) < TC.maxSpellLevel);
        if (upgradeable.length > 0) {
          const picks = upgradeable.sort(() => Math.random() - 0.5)
            .slice(0, 3)
            .map(sp => ({ ...sp, upgradeLevel: (levels[sp.id] ?? 1) + 1 }));
          ctx.setMagicSelectSpells({ spells: picks, isUpgrade: true });
          ctx.setGameState('magicselect');
        }
      } else {
        const pool = SPELLS.filter(sp => !owned.has(sp.id));
        const picks = [];
        const remaining = [...pool];
        for (let i = 0; i < Math.min(3, remaining.length); i++) {
          const sp = weightedSpellPick(remaining);
          picks.push(sp);
          remaining.splice(remaining.indexOf(sp), 1);
        }
        if (picks.length > 0) {
          ctx.setMagicSelectSpells({ spells: picks, isUpgrade: false });
          ctx.setGameState('magicselect');
        }
      }
    }
  }
}

export function updatePhantomStride(s, timestamp) {
  if (!s.phantomStrideActive) return;
  if (s.phantomStrideAt === 0) {
    s.phantomStrideAt = timestamp + CFG.combat.phantomStrideIntervalMs;
  } else if (timestamp >= s.phantomStrideAt) {
    const ox = s.player.x + PLAYER_SIZE / 2;
    const oy = s.player.y + PLAYER_SIZE / 2;
    s.player.x = WORLD_WIDTH  / 2 - PLAYER_SIZE / 2;
    s.player.y = WORLD_HEIGHT / 2 - PLAYER_SIZE / 2;
    s.spellEffects.push(
      { id: `se_ps1_${timestamp}`, type: 'blink', x: ox,              y: oy,               startTime: timestamp,      duration: 350 },
      { id: `se_ps2_${timestamp}`, type: 'blink', x: WORLD_WIDTH / 2, y: WORLD_HEIGHT / 2, startTime: timestamp + 60, duration: 350 },
    );
    s.phantomStrideAt = timestamp + CFG.combat.phantomStrideIntervalMs;
  }
}

export function updateTierBossSpawn(s, timestamp) {
  if (!s.tierBossWarning || s.tierBossAlive) return;
  const warnElapsed = (timestamp - s.tierBossWarning.startTime) / 1000;
  if (warnElapsed < TIER_BOSS_WARNING_SEC) return;

  const tb    = CFG.tierBoss;
  const tidx  = s.tierBossWarning.tier;
  s.tierBossWarning = null;
  s.tierBossAlive   = true;
  const tbDmg = Math.round(ENEMY_DAMAGE * (1 + tidx * CFG.enemies.damageScalePerTier) * tb.dmgMult);
  s.enemies.push({
    id:            s.nextEnemyId++,
    x:             WORLD_WIDTH / 2 - TIER_BOSS_SIZE / 2,
    y:             20,
    hp:            5 * (tidx + 1) * tb.hpMult,
    maxHp:         5 * (tidx + 1) * tb.hpMult,
    isTierBoss:    true,
    size:          TIER_BOSS_SIZE,
    tierIdx:       tidx,
    armor:         Math.min(tb.armorMax, tb.armorBase + tidx * tb.armorPerTier),
    critRate:      tb.critRate,
    critDamage:    tb.critDamage,
    damageType:    'physical',
    damage:        tbDmg,
    lastSwingTime: -(CFG.tierBoss?.swingCd ?? 3000),
  });
  s.spellEffects.push({
    id: `se_tbspawn_${timestamp}`, type: 'explosion',
    x: WORLD_WIDTH / 2, y: 0, radius: 120, startTime: timestamp, duration: 600,
  });
}

export function initBossTimer(s, timestamp) {
  if (s.nextBossSpawnAt === 0) s.nextBossSpawnAt = timestamp + BOSS_SPAWN_INTERVAL;
}
